/**
 * Contractor edit-request mutation/query hooks (ZEUS-130/144).
 *
 * Real-API only — a deliberate scope decision, not an oversight. No
 * demo-mode simulation exists yet (unlike project-creation-request, which
 * started the same way and got demo support added later when asked — see
 * mutations.ts's submitProjectCreationRequest). Callers must gate the
 * "Request edit" trigger and the review panel on `!demoModeActive()`.
 */

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { api } from "./api";
import type { ProjectEditRequest } from "./api";
import { toNumericId } from "./useProjectCreation";

function extractError(e: unknown): string {
  if (e instanceof AxiosError) {
    const data = e.response?.data as { error?: string; message?: string } | undefined;
    return data?.message || data?.error || e.message;
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

function invalidateAfterMutation(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string | number,
) {
  // ZEUS-141 gotcha: prefix key only (no second element) so this matches
  // the cached programme-data query regardless of the exact programmeId.
  queryClient.invalidateQueries({ queryKey: ["programme-data"] });
  queryClient.invalidateQueries({ queryKey: ["project-governance-history", String(projectId)] });
  queryClient.invalidateQueries({ queryKey: ["project-edit-requests", String(projectId)] });
}

export interface SubmitProjectEditRequestHookInput {
  justification: string;
  changes: Partial<{
    name: string;
    description: string;
    region: string;
    district: string;
    budgeted: number;
  }>;
}

export function useSubmitProjectEditRequest() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  async function submitProjectEditRequest(
    projectId: string | number,
    input: SubmitProjectEditRequestHookInput,
  ): Promise<ProjectEditRequest | null> {
    const numericId = toNumericId(projectId);
    if (numericId === undefined) return null;

    setIsPending(true);
    setError(null);
    try {
      const result = await api.submitProjectEditRequest({
        projectId: numericId,
        justification: input.justification,
        changes: input.changes,
      });
      invalidateAfterMutation(queryClient, projectId);
      return result;
    } catch (e) {
      setError(extractError(e));
      return null;
    } finally {
      setIsPending(false);
    }
  }

  return { submitProjectEditRequest, isPending, error };
}

/**
 * The stratus_admin review queue for one project. Real-API only (see
 * file-level note) — callers should not render this in demo mode at all.
 */
export function useProjectEditRequests(projectId: string | number, status = "pending") {
  const numericId = toNumericId(projectId);
  return useQuery({
    queryKey: ["project-edit-requests", String(projectId), status],
    queryFn: () => api.getProjectEditRequests(numericId!, status),
    enabled: numericId !== undefined,
    staleTime: 30_000,
  });
}

export function useApproveProjectEditRequest() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  async function approveProjectEditRequest(
    id: number | string,
    projectId: string | number,
  ): Promise<ProjectEditRequest | null> {
    setIsPending(true);
    setError(null);
    try {
      const result = await api.approveProjectEditRequest(id);
      invalidateAfterMutation(queryClient, projectId);
      return result;
    } catch (e) {
      setError(extractError(e));
      return null;
    } finally {
      setIsPending(false);
    }
  }

  return { approveProjectEditRequest, isPending, error };
}

export function useRejectProjectEditRequest() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  async function rejectProjectEditRequest(
    id: number | string,
    reason: string,
    projectId: string | number,
  ): Promise<ProjectEditRequest | null> {
    setIsPending(true);
    setError(null);
    try {
      const result = await api.rejectProjectEditRequest(id, reason);
      invalidateAfterMutation(queryClient, projectId);
      return result;
    } catch (e) {
      setError(extractError(e));
      return null;
    } finally {
      setIsPending(false);
    }
  }

  return { rejectProjectEditRequest, isPending, error };
}
